// Material UI Components
import { useContext } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@mui/styles";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import Badge from "@mui/material/Badge";
import Tooltip from "@mui/material/Tooltip";
import useScrollTrigger from "@mui/material/useScrollTrigger";

// My Files
import UserMenu from "./UserMenu";
import HamburgerMenu, { DESKTOP_NAV_WIDTH } from "./HamburgerMenu";
import { DatabaseContext, UserContext } from "../../context";
import TransferMenu from "./TransferMenu";
import UploadsMenu from "./UploadsMenu";
import { countUnreadMessages } from "../messages/messageReadReceipts";
import {
    BRAND_GRADIENT,
    BRAND_GREEN,
    BRAND_HAIRLINE,
} from "./brandSurface";

const useStyles = makeStyles((theme) => ({
    root: ({ pinnedNavigation }) => ({
        flexGrow: 1,
        ...(pinnedNavigation && {
            "& + *": {
                [theme.breakpoints.up("md")]: {
                    marginLeft: DESKTOP_NAV_WIDTH,
                },
            },
        }),
    }),
    title: {
        flexGrow: 1,
        minWidth: 0,
    },
}));

export default function NavigationBar({
    navBarTitle = "",
    pinnedNavigation = false,
}) {
    const classes = useStyles({ pinnedNavigation });
    const history = useHistory();
    const { currentUserData } = useContext(UserContext);

    const { conversations } = useContext(DatabaseContext);

    // The bar sits flat against the page until there is content scrolled behind
    // it, then it earns a shadow.
    const isScrolled = useScrollTrigger({
        disableHysteresis: true,
        threshold: 0,
    });

    const userCanOpenMessages =
        currentUserData?.permissions?.openMessage === true;
    const unreadMessages = countUnreadMessages(
        conversations,
        currentUserData?.key,
    );

    // Call sites pass titles as "| Files", where the pipe separated them from a
    // "Nodiant" prefix the bar no longer shows. Home passes nothing and keeps no
    // title at all, so the bar there is just the mark and the actions.
    const barTitle = navBarTitle.replace(/^\s*\|\s*/, "").trim();

    function handleMessagesClick() {
        history.replace("/messages");
    }

    return (
        <div className={classes.root}>
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    ml: pinnedNavigation
                        ? { md: `${DESKTOP_NAV_WIDTH}px` }
                        : 0,
                    width: pinnedNavigation
                        ? { md: `calc(100% - ${DESKTOP_NAV_WIDTH}px)` }
                        : "100%",
                    color: "#fff",
                    backgroundColor: BRAND_GREEN,
                    backgroundImage: BRAND_GRADIENT,
                    borderBottom: BRAND_HAIRLINE,
                    boxShadow: isScrolled
                        ? "0 2px 14px rgba(0, 60, 25, 0.22)"
                        : "none",
                    transition: "box-shadow 180ms ease",
                }}
            >
                <Toolbar
                    sx={{
                        minHeight: { xs: 60, sm: 64 },
                        px: { xs: 1.25, sm: 2.5 },
                    }}
                >
                    <HamburgerMenu pinned={pinnedNavigation} />
                    <Box
                        component="img"
                        src="/images/logo512_inverted.png"
                        alt=""
                        aria-hidden="true"
                        sx={{
                            display: pinnedNavigation
                                ? { xs: "block", md: "none" }
                                : "block",
                            width: 28,
                            height: 28,
                            mr: 1.25,
                            flexShrink: 0,
                            borderRadius: 2,
                        }}
                    />
                    <Typography
                        variant="h6"
                        className={classes.title}
                        noWrap
                    >
                        {barTitle}
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.25,
                            ml: 0.5,
                        }}
                    >
                        {userCanOpenMessages && unreadMessages > 0 && (
                            <Tooltip
                                title={
                                    "You have " +
                                    unreadMessages +
                                    " unread messages"
                                }
                            >
                                <IconButton
                                    aria-label={
                                        unreadMessages + " unread messages"
                                    }
                                    color="inherit"
                                    onClick={handleMessagesClick}
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 2,
                                        color: "rgba(255, 255, 255, 0.94)",
                                        "&:hover": {
                                            backgroundColor:
                                                "rgba(255, 255, 255, 0.14)",
                                        },
                                    }}
                                >
                                    <Badge
                                        badgeContent={unreadMessages}
                                        color="secondary"
                                    >
                                        <ChatBubbleOutlineIcon />
                                    </Badge>
                                </IconButton>
                            </Tooltip>
                        )}
                        <UploadsMenu />
                        <TransferMenu />
                        <UserMenu />
                    </Box>
                </Toolbar>
            </AppBar>
            <Toolbar sx={{ minHeight: { xs: 60, sm: 64 } }} />
        </div>
    );
}
